/**
 * Lakeside water — the "bastu vid vattnet" composition.
 *
 * Two quality paths, chosen by the runtime profile:
 *
 *   • `reflections` (cinematic): a real planar reflection via drei's
 *     `MeshReflectorMaterial`. The sauna itself mirrors in the surface.
 *     Like the transmission-glass pass, it re-renders the scene from a
 *     mirrored camera into an FBO — ~24% of the per-frame cost — so it is
 *     reserved for cinematic mode (see viewerConfig + the perf note).
 *   • otherwise (interactive): a cheap glossy plane that reflects only the
 *     scene environment map (sky + sun). No extra scene render.
 *
 * The shoreline is ORGANIC, not a straight rectangle edge: the geometry's
 * near (shore-facing) edge meanders via a sum-of-sines into bays and points
 * (`shorelineRecede`), and a matching SAND band hugs that same curve —
 * wet sand at the waterline fading to dry sand and then into the grass. The
 * edge only ever recedes INTO the water (never out over the lawn), so it
 * can't push grass blades through the surface and it leaves a natural sandy
 * bank between the grass disc and the waterline.
 *
 * Both paths share one shader patch (`lakeWaterShader.ts`): clear water
 * with a pebble bed seen through moving ripples, caustics and depth colour.
 * The low tier (`detail` off) compiles its lite variant.
 *
 * Demand-mode honesty (per docs/notes/2026-04-11-demand-mode-viewers-
 * must-gate-idle-ambience-loops.md): the ripple clock advances in `useFrame`
 * but never calls `invalidate()` here, so in the standard viewer the water
 * moves while anything repaints (an orbit, a slider) and rests with the
 * camera. The explicit Cinematic presentation opts into a throttled idle
 * repaint (`ambientMotion`) so the lake keeps moving on its own there.
 *
 * The meshes are named `environment-water` / `environment-sand` (not
 * `dressing-*`) so they stay outside the dressing mesh-budget cap.
 */
import { MeshReflectorMaterial } from "@react-three/drei";
import { useFrame, useThree } from "@react-three/fiber";
import { useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { resolveAmbientEffectTime } from "./ambientEffectClock";
import { createShoreSandGeometry, createShoreWaterGeometry } from "./lakeShore";
import {
	applyLakeWater,
	createLakeWaterUniforms,
	LAKE_BED_GLSL,
	LAKE_LITE_DEFINE,
	lakeUniformsOf,
} from "./lakeWaterShader";
import type { WaterPlacement } from "./SceneEnvironment.helpers";

interface SceneWaterProps {
	/** Render water at this site, including the low-quality lakeside view. */
	enabled: boolean;
	/** Use the real planar reflection instead of the env-map glossy plane. */
	reflections: boolean;
	/** Full procedural lake; false draws the low-tier lite shader. */
	detail?: boolean;
	/** World placement + size from `resolveWaterPlacement`. */
	placement: WaterPlacement;
	/** Drop the water tone after sundown so it reads as dark, still water. */
	isEvening: boolean;
	/**
	 * Reflection-FBO resolution for the planar-reflection path. 512 is
	 * plenty for a calm, blurred lake — the reflection runs `blur` over it
	 * anyway — and costs 4× less fragment work than 1024 in the extra
	 * full-scene reflection pass.
	 */
	reflectionResolution?: number;
	/**
	 * Keep the ripples moving with the camera at rest by repainting at a
	 * throttled rate. Only for the explicit Cinematic presentation — the
	 * standard viewer must stay idle on the demand frameloop.
	 */
	ambientMotion?: boolean;
}

/** Idle repaint rate for `ambientMotion` — smooth enough for slow swell. */
const AMBIENT_MOTION_FPS = 30;

/** Reflector materials already routed through the lake shader. */
const reflectorPatched = new WeakSet<THREE.Material>();

// The shader owns the water colour (bed, absorption, scatter); the base
// colour only feeds the reflector's own mixing and must not tint the bed.
const WATER_COLOR = 0xffffff;
const SCATTER_DAY = new THREE.Color(0.03, 0.075, 0.075);
const SCATTER_EVENING = new THREE.Color(0.006, 0.016, 0.02);

/**
 * Shade + fade the sand band: wet (dark) sand at the waterline → dry (light)
 * sand inland, with the inland edge fading to transparent so it blends into
 * the grass (`aSand`: 1 at water → 0 inland).
 */
function applySandShading(
	material: THREE.MeshStandardMaterial,
	lite: boolean,
): void {
	material.onBeforeCompile = (shader) => {
		shader.vertexShader = shader.vertexShader.replace(
			"#include <common>",
			`#include <common>
attribute float aSand;
varying float vSand;
varying vec3 vSandWorld;`,
		);
		shader.vertexShader = shader.vertexShader.replace(
			"#include <begin_vertex>",
			`#include <begin_vertex>
vSand = aSand;
vSandWorld = (modelMatrix * vec4(transformed, 1.0)).xyz;`,
		);
		shader.fragmentShader = shader.fragmentShader.replace(
			"#include <common>",
			`#include <common>
varying float vSand;
varying vec3 vSandWorld;
${lite ? LAKE_LITE_DEFINE : ""}
${LAKE_BED_GLSL}`,
		);
		shader.fragmentShader = shader.fragmentShader.replace(
			"#include <color_fragment>",
			`#include <color_fragment>
{
  vec3 drySand = vec3(0.22, 0.20, 0.15);
  vec3 wetSand = vec3(0.085, 0.08, 0.06);
  diffuseColor.rgb = mix(drySand, wetSand, smoothstep(0.55, 1.0, vSand));
  // The lake's pebble bed runs out of the water onto the wet bank.
  vec2 sandFw = fwidth(vSandWorld.xz);
  vec3 bank = lakePebbles(vSandWorld.xz, max(sandFw.x, sandFw.y), 0.55) * 0.6;
  diffuseColor.rgb = mix(diffuseColor.rgb, bank, smoothstep(0.45, 1.0, vSand) * 0.8);
  // Opaque from the waterline through the band, fading out at the inland edge.
  diffuseColor.a *= smoothstep(0.0, 0.65, vSand);
}`,
		);
	};
	material.customProgramCacheKey = () =>
		lite ? "lake-sand-v2|lite" : "lake-sand-v2";
	material.needsUpdate = true;
}

export function SceneWater({
	enabled,
	reflections,
	detail = true,
	placement,
	isEvening,
	reflectionResolution = 512,
	ambientMotion = false,
}: SceneWaterProps) {
	const [xExtent, zExtent] = placement.size;
	const invalidate = useThree((state) => state.invalidate);

	// One uniforms object for the lifetime of the lake: R3F keeps a compiled
	// program's uniforms, so values are written in place, never swapped.
	// Values are written through the drawn mesh's material (see
	// `lakeUniformsOf`), the same route the chimney smoke uses.
	const uniforms = useMemo(() => createLakeWaterUniforms(), []);
	const waterRef = useRef<THREE.Mesh>(null);
	useEffect(() => {
		const material = waterRef.current?.material;
		const lake = lakeUniformsOf(material);
		if (lake) {
			lake.uLakeScatter.value.copy(isEvening ? SCATTER_EVENING : SCATTER_DAY);
			lake.uLakeCaustics.value = isEvening ? 0.12 : 1;
		}
		// After sundown the IBL still carries a daylight sky; on rippled water
		// it read as white chop under a pink dusk. Let the dome's tone lead.
		// (The reflector keeps its own, already lower, env share.)
		if (material instanceof THREE.MeshStandardMaterial && !reflections) {
			material.envMapIntensity = isEvening ? 0.5 : 1;
		}
		invalidate();
	}, [isEvening, reflections, invalidate]);

	useFrame((state) => {
		const lake = lakeUniformsOf(waterRef.current?.material);
		if (lake) {
			lake.uLakeTime.value = resolveAmbientEffectTime(state.clock.elapsedTime);
		}
	});

	useEffect(() => {
		if (!enabled || !ambientMotion) return;
		const reduceMotion =
			typeof window !== "undefined" &&
			window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
		if (reduceMotion) return;
		const id = window.setInterval(() => {
			if (document.visibilityState === "visible") invalidate();
		}, 1000 / AMBIENT_MOTION_FPS);
		return () => window.clearInterval(id);
	}, [enabled, ambientMotion, invalidate]);

	const geometry = useMemo(
		() => createShoreWaterGeometry(xExtent, zExtent),
		[xExtent, zExtent],
	);
	useEffect(() => () => geometry.dispose(), [geometry]);

	const sandGeometry = useMemo(
		() => createShoreSandGeometry(Math.min(30, xExtent), zExtent),
		[xExtent, zExtent],
	);
	useEffect(() => () => sandGeometry.dispose(), [sandGeometry]);

	// Interactive path: the lake shader on a glossy standard material that
	// reflects the scene environment map (sky + sun). Lazy + disposable.
	const glossyMaterial = useMemo(
		() =>
			applyLakeWater(
				new THREE.MeshStandardMaterial({
					color: WATER_COLOR,
					roughness: 0.06,
					metalness: 0,
					envMapIntensity: 1,
					side: THREE.DoubleSide,
				}),
				uniforms,
				{ lite: !detail },
			),
		[uniforms, detail],
	);
	useEffect(() => () => glossyMaterial.dispose(), [glossyMaterial]);

	// Sand band material: matte, slightly env-lit, blended over the lawn.
	const sandMaterial = useMemo(() => {
		const material = new THREE.MeshStandardMaterial({
			color: 0xcaba93,
			roughness: 0.94,
			metalness: 0,
			envMapIntensity: 0.3,
			transparent: true,
			depthWrite: false,
			side: THREE.DoubleSide,
		});
		applySandShading(material, !detail);
		return material;
	}, [detail]);
	useEffect(() => () => sandMaterial.dispose(), [sandMaterial]);

	if (!enabled) return null;

	// Sand sits just above the ground (and the earth-halo overlays) but below
	// the water surface, so the water always wins where they meet.
	const sandPosition: [number, number, number] = [
		placement.center[0],
		placement.center[1] - 0.01,
		placement.center[2],
	];

	const waterMesh = reflections ? (
		<mesh ref={waterRef} name="environment-water" position={placement.center}>
			<primitive object={geometry} attach="geometry" />
			<MeshReflectorMaterial
				ref={(material: THREE.MeshStandardMaterial | null) => {
					if (!material || reflectorPatched.has(material)) return;
					reflectorPatched.add(material);
					applyLakeWater(material, uniforms);
				}}
				resolution={reflectionResolution}
				mirror={1}
				blur={[180, 70]}
				mixBlur={2.5}
				mixStrength={1}
				mixContrast={1}
				depthScale={1}
				minDepthThreshold={0.4}
				maxDepthThreshold={1.2}
				color={WATER_COLOR}
				metalness={0}
				roughness={0.06}
				// The mirror already carries the sky; keep the env share small
				// so grazing water is not reflected twice.
				envMapIntensity={0.35}
				side={THREE.DoubleSide}
			/>
		</mesh>
	) : (
		<mesh ref={waterRef} name="environment-water" position={placement.center}>
			<primitive object={geometry} attach="geometry" />
			<primitive object={glossyMaterial} attach="material" />
		</mesh>
	);

	return (
		<>
			<mesh
				name="environment-sand"
				position={sandPosition}
				renderOrder={3}
				castShadow={false}
				receiveShadow
			>
				<primitive object={sandGeometry} attach="geometry" />
				<primitive object={sandMaterial} attach="material" />
			</mesh>
			{waterMesh}
		</>
	);
}
